import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { getBackendUrl } from "../config";

const BACKEND_URL = getBackendUrl();

const ProductionPlanningEmailRecipients = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [companyFilter, setCompanyFilter] = useState("");
  const [search, setSearch] = useState("");

  const isDark = document.documentElement.classList.contains("dark");
  const bgCard = isDark ? "bg-gray-800" : "bg-white";
  const borderColor = isDark ? "border-gray-700" : "border-gray-200";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-600";

  const companyNameById = useMemo(() => {
    const map = new Map();
    companies.forEach((c) => map.set(c.id, c.name || c.id));
    return map;
  }, [companies]);

  const getUserCompanyNames = (user) => {
    const ids = user.company_ids?.length
      ? user.company_ids
      : user.company_id
        ? [user.company_id]
        : [];
    return ids.map((id) => companyNameById.get(id) || id).join(", ");
  };

  const eligibleUsers = useMemo(() => {
    return users.filter((u) => {
      const email = (u.email || "").trim();
      return email.includes("@");
    });
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return eligibleUsers.filter((u) => {
      if (companyFilter) {
        const ids = u.company_ids?.length
          ? u.company_ids
          : u.company_id
            ? [u.company_id]
            : [];
        if (!ids.includes(companyFilter)) return false;
      }
      if (!q) return true;
      const hay = [
        u.username,
        u.email,
        u.first_name,
        u.last_name,
        u.role,
        getUserCompanyNames(u),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [eligibleUsers, companyFilter, search, companyNameById]);

  const selectedInViewCount = filteredUsers.filter((u) =>
    selectedUserIds.has(u.id),
  ).length;
  const allInViewSelected =
    filteredUsers.length > 0 && selectedInViewCount === filteredUsers.length;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      try {
        const [usersRes, companiesRes, recipientsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/users`, auth),
          axios.get(`${BACKEND_URL}/api/companies`, auth),
          axios.get(
            `${BACKEND_URL}/api/planning/production-planning-email-recipients`,
            auth,
          ),
        ]);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
        const cfg = recipientsRes.data || {};
        setConfigured(Boolean(cfg.configured));
        setSelectedUserIds(new Set(cfg.user_ids || []));
      } catch (e) {
        console.error("Error loading email recipients:", e);
        toast.error(
          e.response?.data?.detail || t("planningEmailRecipientsLoadError"),
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [t]);

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAllInView = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (allInViewSelected) {
        filteredUsers.forEach((u) => next.delete(u.id));
      } else {
        filteredUsers.forEach((u) => next.add(u.id));
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem("access_token");
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/planning/production-planning-email-recipients`,
        { user_ids: Array.from(selectedUserIds) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setConfigured(Boolean(res.data?.configured));
      setSelectedUserIds(new Set(res.data?.user_ids || []));
      toast.success(t("planningEmailRecipientsSaved"));
    } catch (e) {
      console.error("Save recipients error:", e);
      toast.error(
        e.response?.data?.detail || t("planningEmailRecipientsSaveError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDispatchNow = async () => {
    if (!configured) {
      toast.error(t("planningEmailRecipientsSaveFirst"));
      return;
    }
    if (selectedUserIds.size === 0) {
      toast.error(t("planningEmailRecipientsNoneSelected"));
      return;
    }
    setDispatching(true);
    const token = localStorage.getItem("access_token");
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/planning/dispatch-production-planning-emails`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const sent = res.data?.emails_sent ?? 0;
      toast.success(
        t("planningEmailDispatchDone").replace("{count}", String(sent)),
      );
    } catch (e) {
      console.error("Dispatch emails error:", e);
      toast.error(e.response?.data?.detail || t("planningEmailDispatchError"));
    } finally {
      setDispatching(false);
    }
  };

  return (
    <div
      className={`${bgCard} rounded-xl p-4 md:p-5 shadow-sm border ${borderColor} mb-6`}
    >
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("planningEmailRecipientsTitle")}
          </h2>
          <p className={`text-sm mt-1 ${textSecondary}`}>
            {t("planningEmailRecipientsHint")}
          </p>
          {!configured && (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
              {t("planningEmailRecipientsNotConfigured")}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={toggleAllInView}
            disabled={loading || filteredUsers.length === 0}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {allInViewSelected
              ? t("planningEmailRecipientsUncheckAll")
              : t("planningEmailRecipientsCheckAll")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || saving}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? t("saving") : t("save")}
          </button>
          <button
            type="button"
            onClick={handleDispatchNow}
            disabled={loading || dispatching || saving}
            className="px-4 py-2 text-sm font-medium border border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50"
          >
            {dispatching ? t("loading") : t("planningEmailDispatchNow")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("planningEmailRecipientsSearch")}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        />
        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          <option value="">{t("planningEmailRecipientsAllCompanies")}</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <p className={`text-xs mb-3 ${textSecondary}`}>
        {t("planningEmailRecipientsSelectedCount")
          .replace("{selected}", String(selectedUserIds.size))
          .replace("{visible}", String(filteredUsers.length))}
      </p>

      {loading ? (
        <div className="text-center py-6 text-gray-500">{t("loading")}</div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-6 text-gray-500">
          {t("planningEmailRecipientsNoUsers")}
        </div>
      ) : (
        <div className="max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
          {filteredUsers.map((u) => {
            const checked = selectedUserIds.has(u.id);
            const fullName = [u.first_name, u.last_name]
              .filter(Boolean)
              .join(" ");
            return (
              <label
                key={u.id}
                className="flex items-start gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleUser(u.id)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900 dark:text-white">
                    {fullName || u.username}
                    <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                      {u.role}
                    </span>
                  </span>
                  <span className="block text-sm text-gray-600 dark:text-gray-300">
                    {u.email}
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {getUserCompanyNames(u) || "—"}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductionPlanningEmailRecipients;
